import type { AdminDashboardVariables } from "@/admin/entities/adminDashboard";

/**
 * Период дашборда (спека §3.1). Даты в адресе это дни UTC включительно
 * (`?preset=currentMonth&from=2026-10-01&to=2026-10-15`), в запрос уходит
 * полуоткрытый интервал: конец это полночь UTC следующего дня. Пресет
 * пересчитывается от сегодняшнего дня того, кто открыл ссылку; свой
 * период хранит даты как есть.
 */
export const PERIOD_PRESETS = [
  "today",
  "7d",
  "30d",
  "currentMonth",
  "previousMonth",
  "quarter",
  "year",
  "allTime",
] as const;

export type PeriodPreset = (typeof PERIOD_PRESETS)[number] | "custom";

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  today: "Сегодня",
  "7d": "7 дней",
  "30d": "30 дней",
  currentMonth: "Текущий месяц",
  previousMonth: "Прошлый месяц",
  quarter: "Квартал",
  year: "Год",
  allTime: "Всё время",
  custom: "Свой период",
};

export const DEFAULT_PRESET = "currentMonth" satisfies PeriodPreset;

/** Предел бэкенда: не больше пяти лет за запрос. */
const MAX_RANGE_MONTHS = 5 * 12;

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface DayRange {
  from: string;
  to: string;
}

export interface DashboardPeriod {
  preset: PeriodPreset;
  range: DayRange;
  compare: DayRange | null;
}

export function toDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function parseDay(value: string | null | undefined): Date | null {
  if (!value || !DAY_PATTERN.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || toDay(date) !== value) return null;
  return date;
}

function startOfUtcDay(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
}

function addDays(date: Date, days: number): Date {
  return new Date(startOfUtcDay(date).getTime() + days * DAY_MS);
}

/** Сдвиг на месяцы с поджатием дня к концу месяца: 31 марта минус месяц это 28 или 29 февраля. */
export function addMonthsClamped(date: Date, months: number): Date {
  const start = startOfUtcDay(date);
  const target = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1)
  );
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  target.setUTCDate(Math.min(start.getUTCDate(), lastDay));
  return target;
}

function rangeOf(from: Date, to: Date): DayRange {
  return { from: toDay(from), to: toDay(to) };
}

function isPreset(value: string): value is (typeof PERIOD_PRESETS)[number] {
  return (PERIOD_PRESETS as readonly string[]).includes(value);
}

export function presetRange(
  preset: Exclude<PeriodPreset, "custom">,
  today: Date
): DayRange {
  const day = startOfUtcDay(today);
  const year = day.getUTCFullYear();
  const month = day.getUTCMonth();

  switch (preset) {
    case "today":
      return rangeOf(day, day);
    case "7d":
      return rangeOf(addDays(day, -6), day);
    case "30d":
      return rangeOf(addDays(day, -29), day);
    case "currentMonth":
      return rangeOf(new Date(Date.UTC(year, month, 1)), day);
    case "previousMonth":
      return rangeOf(
        new Date(Date.UTC(year, month - 1, 1)),
        new Date(Date.UTC(year, month, 0))
      );
    case "quarter":
      return rangeOf(new Date(Date.UTC(year, month - (month % 3), 1)), day);
    case "year":
      return rangeOf(new Date(Date.UTC(year, 0, 1)), day);
    case "allTime":
      return rangeOf(addMonthsClamped(addDays(day, 1), -MAX_RANGE_MONTHS), day);
  }
}

function shiftMonths(range: DayRange, months: number): DayRange | null {
  const from = parseDay(range.from);
  const to = parseDay(range.to);
  if (!from || !to) return null;
  return rangeOf(addMonthsClamped(from, months), addMonthsClamped(to, months));
}

export function comparisonRange(
  preset: PeriodPreset,
  range: DayRange
): DayRange | null {
  const from = parseDay(range.from);
  const to = parseDay(range.to);
  if (!from || !to) return null;

  switch (preset) {
    case "allTime":
      return null;
    case "currentMonth":
      return shiftMonths(range, -1);
    case "quarter":
      return shiftMonths(range, -3);
    case "year":
      return shiftMonths(range, -12);
    case "previousMonth":
      return rangeOf(
        addMonthsClamped(from, -1),
        new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 0))
      );
    default: {
      const length = Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1;
      return rangeOf(addDays(from, -length), addDays(from, -1));
    }
  }
}

function buildPeriod(preset: PeriodPreset, range: DayRange): DashboardPeriod {
  return { preset, range, compare: comparisonRange(preset, range) };
}

export function presetPeriod(
  preset: Exclude<PeriodPreset, "custom">,
  today: Date
): DashboardPeriod {
  return buildPeriod(preset, presetRange(preset, today));
}

/** Свой период: даты по порядку и не длиннее пяти лет. */
export function customPeriod(range: DayRange): DashboardPeriod {
  let from = parseDay(range.from);
  let to = parseDay(range.to);
  if (!from || !to) return buildPeriod("custom", range);
  if (from.getTime() > to.getTime()) [from, to] = [to, from];
  const earliest = addMonthsClamped(addDays(to, 1), -MAX_RANGE_MONTHS);
  if (from.getTime() < earliest.getTime()) from = earliest;
  return buildPeriod("custom", rangeOf(from, to));
}

export function parsePeriod(
  params: URLSearchParams,
  today: Date
): DashboardPeriod {
  const preset = params.get("preset");
  if (preset && isPreset(preset)) return presetPeriod(preset, today);

  if (preset === "custom") {
    const from = parseDay(params.get("from"));
    const to = parseDay(params.get("to"));
    if (from && to) return customPeriod(rangeOf(from, to));
  }

  return presetPeriod(DEFAULT_PRESET, today);
}

export function periodToSearchParams(
  period: DashboardPeriod
): Record<string, string> {
  return {
    preset: period.preset,
    from: period.range.from,
    to: period.range.to,
  };
}

const startOf = (day: string) => `${day}T00:00:00.000Z`;

function endOf(day: string): string {
  const date = parseDay(day);
  return date ? `${toDay(addDays(date, 1))}T00:00:00.000Z` : startOf(day);
}

export function toQueryVariables(
  period: DashboardPeriod
): AdminDashboardVariables {
  return {
    dateFrom: startOf(period.range.from),
    dateTo: endOf(period.range.to),
    compareFrom: period.compare ? startOf(period.compare.from) : null,
    compareTo: period.compare ? endOf(period.compare.to) : null,
  };
}
